import type { ItemPhase } from '@/design-system';
import type { FindingRecord } from '@/shared/offline';
import {
    addCosts,
    amountToCents,
    isManualSeverity,
    isUrgency,
    severityOf,
    totalCents,
    urgencyOf,
    ZERO_COSTS,
    type AnswerOverrides,
    type CostCents,
    type FindingSeverity,
    type Urgency,
} from '@/entities/finding';

import {
    isAnswered,
    type AmountPrompt,
    type Answer,
    type AnswerKey,
    type CostAmounts,
    type EngineSnapshot,
    type NodeId,
    type PackageQuestion,
} from '../engine';
import { nodeIdFromWire } from './wire-keys';

/**
 * Van engine-uitkomst naar scherm. Alleen vormgeving: geen zichtbaarheids- of
 * kostenlogica - die hoort in `../engine`, zodat client en API gegarandeerd
 * hetzelfde rekenen.
 */

/* -------------------------------------------------------------------------- */
/* Boom                                                                       */
/* -------------------------------------------------------------------------- */

export interface TreeRow {
    nodeId: NodeId;
    name: string;
    /** Het normnummer uit de template, anders het boompad. */
    number: string;
    depth: number;
    /** Een sectiekop: heeft kinderen en stelt zelf geen vragen. */
    isSection: boolean;
    answered: number;
    visible: number;
    /** Alleen op een sectiekop: onderdelen klaar / onderdelen totaal. */
    childrenDone: number;
    childrenTotal: number;
    phase: ItemPhase;
}

/**
 * Klaar is een onderdeel waar de wandeling niet meer stopt: ook een onderdeel dat
 * zelf niets meer vraagt omdat een eerder antwoord de rest dichthoudt ("Elektra"
 * niet gekozen). De tellers alleen kunnen dat niet zeggen, want een vraag die in
 * een eerder onderdeel hoort, telt daar.
 */
function phaseOf(answered: number, complete: boolean): ItemPhase {
    if (complete) {
        return 'klaar';
    }

    return answered === 0 ? 'open' : 'bezig';
}

export function treeRows(snapshot: EngineSnapshot): TreeRow[] {
    const rows: TreeRow[] = [];

    for (const nodeId of snapshot.tree.order) {
        const runtime = snapshot.tree.nodes.get(nodeId);
        const node = snapshot.visibility.nodes.get(nodeId);
        const progress = snapshot.progress.nodes.get(nodeId);

        if (!runtime || !node || !progress) {
            continue;
        }

        /* Een multi-container is nooit alleen een kop: daar vul je het aantal in. */
        const isSection =
            !runtime.isMultiContainer &&
            node.childIds.length > 0 &&
            runtime.graphNode.items.length === 0;
        const childrenDone = node.childIds.filter(
            (childId) => snapshot.progress.nodes.get(childId)?.allAnsweredDeep ?? false,
        ).length;

        rows.push({
            nodeId,
            name: node.displayName,
            number: runtime.nhgCode ?? node.sectionPath,
            depth: runtime.depth,
            isSection,
            answered: isSection ? progress.subtreeAnsweredCount : progress.answeredCount,
            visible: isSection ? progress.subtreeVisibleCount : progress.visibleCount,
            childrenDone,
            childrenTotal: node.childIds.length,
            phase: isSection
                ? phaseOf(progress.subtreeAnsweredCount, progress.allAnsweredDeep)
                : phaseOf(progress.answeredCount, progress.allAnswered),
        });
    }

    return rows;
}

/**
 * De regel boven de vragen: "4 vragen beantwoord · 2 vervallen". Die beschrijft
 * wat je hier ziet: van toepassing zijn de vragen op het pad van dit onderdeel,
 * ook een vraag die in een eerder onderdeel hoort en hier als antwoord staat (en
 * daar telt). Vervallen is wat de wandeling hier niet vraagt — omdat een eerder
 * antwoord het wegnam, of omdat dat antwoord er nog niet is. Nooit "overgeslagen":
 * dat klinkt als werk dat de keurmeester liet liggen, terwijl het werk is dat hem
 * bespaard blijft.
 */
export function nodeMeta(
    snapshot: EngineSnapshot,
    nodeId: NodeId,
): { applicable: number; skipped: number } {
    const node = snapshot.visibility.nodes.get(nodeId);
    const runtime = snapshot.tree.nodes.get(nodeId);

    if (!node || !runtime) {
        return { applicable: 0, skipped: 0 };
    }

    return {
        applicable: node.questionKeys.length + (node.multiCountKey ? 1 : 0),
        skipped: Math.max(0, runtime.graphNode.items.length - node.questionKeys.length),
    };
}

/** Het eerste onderdeel dat vragen stelt - waar het scherm op opent. */
export function firstAnsweringNodeId(snapshot: EngineSnapshot): NodeId | null {
    for (const nodeId of snapshot.tree.order) {
        const runtime = snapshot.tree.nodes.get(nodeId);

        if (runtime && runtime.graphNode.items.length > 0) {
            return nodeId;
        }
    }

    return null;
}

/* -------------------------------------------------------------------------- */
/* Vragen                                                                     */
/* -------------------------------------------------------------------------- */

export interface QuestionView {
    key: AnswerKey;
    question: PackageQuestion;
    /** Het onderdeel dat deze vraag toont - het bijschrift van een foto volgt hem. */
    nodeId: NodeId;
    /**
     * Een vraag die in meer onderdelen staat, hoort bij het eerste in de boom waar
     * hij verschijnt. Staat hij daar al, dan is dit dat onderdeel: hier toont het
     * scherm alleen het antwoord. `null` als hij hier gesteld wordt.
     */
    askedIn: { nodeId: NodeId; place: string } | null;
    photoRequired: boolean;
    answered: boolean;
    /** Zoveel foto's moeten er minstens bij een beantwoorde vraag met `Foto verplicht`. */
    photoMin: number;
    selectedOptionIds: readonly number[];
    /** Het antwoord zelf, voor getal- en tekstvragen. */
    answer: Answer | undefined;
    /**
     * Minstens één gekozen optie is een gebrek of aandachtspunt: dezelfde regel
     * als de API, op het bedrag dat de optie telt - met de eigen bedragen van de
     * inspecteur erin.
     */
    isFinding: boolean;
    /**
     * Een gekozen optie heeft geen vast bedrag: de inspecteur vult het zelf in, voor
     * deze urgentie (`amount_prompt`). `null` bij elke andere keuze.
     */
    amountPrompt: AmountPrompt | null;
}

/**
 * De urgentie waarvoor een gevolgde optie van dit antwoord in dit onderdeel om een
 * bedrag vraagt, of `null`.
 */
export function amountPromptOf(
    snapshot: EngineSnapshot,
    nodeId: NodeId,
    key: AnswerKey,
): AmountPrompt | null {
    const chosen = snapshot.visibility.nodes
        .get(nodeId)
        ?.chosen.find((entry) => entry.questionKey === key && entry.option.amount_prompt);

    return chosen?.option.amount_prompt ?? null;
}

/**
 * Heeft de inspecteur zelf een bedrag op dit antwoord gezet, in welke urgentie ook?
 * Dan is een gevraagd bedrag ingevuld: hij mag het naar een andere urgentie zetten.
 */
export function hasOwnAmount(answer: Answer | undefined): boolean {
    return [answer?.direct_cost, answer?.term_cost, answer?.improvement_cost].some(
        (amount) => amount !== null && amount !== undefined,
    );
}

export function questionViews(snapshot: EngineSnapshot, nodeId: NodeId): QuestionView[] {
    const node = snapshot.visibility.nodes.get(nodeId);
    const runtime = snapshot.tree.nodes.get(nodeId);

    if (!node || !runtime) {
        return [];
    }

    const views: QuestionView[] = [];

    node.questionKeys.forEach((key, index) => {
        const questionId = node.questionIds[index];
        const graphQuestion =
            questionId === undefined ? undefined : snapshot.graph.questionsById.get(questionId);

        if (!graphQuestion || questionId === undefined) {
            return;
        }

        const answer = snapshot.answers[key];
        const selectedOptionIds = answer?.option_ids ?? [];
        const owner = snapshot.visibility.questionOwners.get(key) ?? nodeId;

        views.push({
            key,
            question: graphQuestion.question,
            nodeId,
            askedIn: owner === nodeId ? null : { nodeId: owner, place: placeOf(snapshot, owner) },
            photoRequired: graphQuestion.question.photo_requirement === 'required',
            photoMin: Math.max(1, graphQuestion.question.photo_min ?? 1),
            answered: isAnswered(graphQuestion, answer),
            selectedOptionIds,
            answer,
            isFinding: node.chosen.some(
                (entry, position) =>
                    entry.questionKey === key &&
                    severityOf(entry.option.result, countedCosts(snapshot, nodeId, position)) !==
                        'clean',
            ),
            amountPrompt: amountPromptOf(snapshot, nodeId, key),
        });
    });

    return views;
}

/**
 * Het antwoord in één regel, zoals een onderdeel het toont als de vraag elders
 * hoort: de gekozen opties in de volgorde van de vraag, een getal in Nederlandse
 * notatie met zijn eenheid, of de tekst. Leeg zolang er geen antwoord is.
 */
export function answerSummary(view: Pick<QuestionView, 'question' | 'answer'>): string {
    const { question, answer } = view;

    if (!answer) {
        return '';
    }

    switch (question.type) {
        case 'number': {
            const value = answer.value_number;

            if (value === null || value === undefined || !Number.isFinite(value)) {
                return '';
            }

            return [String(value).replace('.', ','), question.number_unit]
                .filter((part) => part !== null && part !== '')
                .join(' ');
        }
        case 'text':
            return answer.value_text ?? '';
        case 'text_items':
            return answer.value_items && answer.value_items.length > 0
                ? answer.value_items.join(' · ')
                : (answer.value_text ?? '');
        default: {
            const chosen = new Set(answer.option_ids ?? []);

            return question.options
                .filter((option) => chosen.has(option.id))
                .map((option) => option.label)
                .join(', ');
        }
    }
}

/* -------------------------------------------------------------------------- */
/* Bevindingen en kosten                                                      */
/* -------------------------------------------------------------------------- */

export interface FindingView {
    /** Uniek per gekozen optie per knoop (zoals `derived_key` in de API), of het `finding_uuid`. */
    key: string;
    source: 'answer' | 'manual';
    /** De vraag waar de bevinding uit volgt; `null` bij een handmatige bevinding. */
    answerKey: AnswerKey | null;
    nodeId: NodeId | null;
    questionId: number | null;
    /** De rapporttekst: de overschrijving, anders de tekst uit de norm, anders het optielabel. */
    label: string;
    /** Waar het gebrek zit, in de woorden van de inspecteur; leeg als er niets staat. */
    location: string;
    /** "25 Dakbedekking · hoofddak" */
    place: string;
    /** In hele centen: wat de gekozen optie telt, met de eigen bedragen van de inspecteur erin. */
    costs: CostCents;
    severity: FindingSeverity;
    urgency: Urgency | null;
    /** Deze bevinding draagt de overschrijvingen van het antwoord (de eerste in boomvolgorde). */
    carriesOverrides: boolean;
    /** Alleen bij een handmatige bevinding. */
    findingUuid: string | null;
}

/** Wat het detailblok van één vraag toont. */
export interface QuestionFinding {
    amountCents: number;
    urgency: Urgency | null;
    nhg: string | null;
    /** Waar het gebrek zit, in de woorden van de inspecteur. */
    location: string;
    /**
     * Het bedrag is dat van de inspecteur zelf, niet dat van de vragenlijst. Het
     * blok zegt dat erbij, zodat hij ziet dat zijn eigen inschatting telt.
     */
    ownAmount: boolean;
}

/**
 * Het onderdeel van een handmatige bevinding in deze boom, of `null`: geen
 * onderdeel meegegeven, of een instantie die er nu niet is ("Berging 3" bij een
 * aantal van 2). De API plaatst hem precies zo (`NodeAddresses`).
 */
export function manualFindingNodeId(
    snapshot: EngineSnapshot,
    record: Pick<FindingRecord, 'nodeId' | 'multiScope'>,
): NodeId | null {
    if (record.nodeId === null || record.nodeId === undefined) {
        return null;
    }

    const nodeId = nodeIdFromWire(record.nodeId, record.multiScope ?? []);

    return snapshot.tree.nodes.has(nodeId) ? nodeId : null;
}

/** Een bevinding die de inspecteur zelf toevoegde. */
export function manualFindingView(snapshot: EngineSnapshot, record: FindingRecord): FindingView {
    const costs: CostCents = {
        direct: amountToCents(record.directCost) ?? 0,
        term: amountToCents(record.termCost) ?? 0,
        improvement: amountToCents(record.improvementCost) ?? 0,
    };
    const severity = isManualSeverity(record.severity) ? record.severity : 'attention';
    const nodeId = manualFindingNodeId(snapshot, record);

    return {
        key: record.findingUuid,
        source: 'manual',
        answerKey: null,
        nodeId,
        questionId: null,
        label: text(record.description) ?? record.title ?? '',
        location: text(record.location) ?? '',
        place: nodeId ? placeOf(snapshot, nodeId) : (record.title ?? ''),
        costs,
        severity,
        urgency: isUrgency(record.urgency) ? record.urgency : null,
        carriesOverrides: false,
        findingUuid: record.findingUuid,
    };
}

/**
 * Wat de gevolgde optie op `position` in `NodeVisibility.chosen` telt: de
 * bedragen van de optie maal de exacte hoeveelheid, afgerond op hele centen, en
 * een eigen bedrag van de inspecteur in plaats van het berekende - één keer per
 * antwoord. Dat rekent de engine, net als `CostAggregator` in de API; hier wordt
 * het alleen gelezen, nooit opnieuw uitgerekend.
 */
function countedCosts(snapshot: EngineSnapshot, nodeId: NodeId, position: number): CostCents {
    return snapshot.costs.nodes.get(nodeId)?.chosen[position] ?? ZERO_COSTS;
}

function text(value: string | null | undefined): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * De bevindingen die uit de antwoorden volgen, precies zoals de API ze afleidt
 * (`DerivedFindingWriter`): één per gekozen optie per knoop, in boomvolgorde,
 * zodra die optie een gebrek of aandachtspunt is of geld draagt.
 *
 * Het bedrag is wat de engine voor die optie telt, dus de bevindingen tellen
 * exact op tot de knooptotalen en het dossiertotaal. Een eigen bedrag op een
 * antwoord telt daarin één keer, op de eerste gekozen optie van dat antwoord in
 * boomvolgorde; op elke volgende telt die urgentie 0. De overige
 * overschrijvingen (`overrides`: tekst en urgentie) staan op diezelfde eerste
 * bevinding - een vraag die in twee onderdelen staat, heeft één antwoord.
 */
export function collectFindings(
    snapshot: EngineSnapshot,
    overrides: ReadonlyMap<AnswerKey, AnswerOverrides>,
): FindingView[] {
    const findings: FindingView[] = [];
    const seen = new Set<AnswerKey>();

    for (const nodeId of snapshot.tree.order) {
        const node = snapshot.visibility.nodes.get(nodeId);

        if (!node) {
            continue;
        }

        for (const [position, chosen] of node.chosen.entries()) {
            const key = chosen.questionKey;
            const override = overrides.get(key);
            const first = override !== undefined && !seen.has(key);

            if (override !== undefined) {
                seen.add(key);
            }

            const graphQuestion = snapshot.graph.questionsById.get(chosen.questionId);
            const costs = countedCosts(snapshot, nodeId, position);
            const severity = severityOf(chosen.option.result, costs);

            if (
                severity === 'clean' &&
                costs.direct === 0 &&
                costs.term === 0 &&
                costs.improvement === 0
            ) {
                continue;
            }

            const overrideUrgency = first ? override.urgency : null;

            findings.push({
                key: `${nodeId}|${key}|${chosen.optionId}`,
                source: 'answer',
                answerKey: key,
                nodeId,
                questionId: chosen.questionId,
                label:
                    (first ? text(override.reportText) : null) ??
                    text(chosen.option.report_text) ??
                    graphQuestion?.optionsById.get(chosen.questionOptionId)?.label ??
                    graphQuestion?.question.label ??
                    key,
                /* De plek hoort bij het antwoord, dus elke bevinding ervan draagt hem. */
                location: text(override?.location) ?? '',
                place: placeOf(snapshot, nodeId),
                costs,
                severity,
                urgency: isUrgency(overrideUrgency)
                    ? overrideUrgency
                    : urgencyOf(chosen.option.result, costs),
                carriesOverrides: first,
                findingUuid: null,
            });
        }
    }

    return findings;
}

/**
 * "25 Dakbedekking · hoofddak" - sectienummer, sectie en onderdeel. Bij een
 * onderdeel dat per exemplaar terugkomt, noemt het exemplaar zich ("Dakkapel 2"):
 * anders staat in "Nog open" twaalf keer dezelfde regel.
 */
export function placeOf(snapshot: EngineSnapshot, nodeId: NodeId): string {
    const runtime = snapshot.tree.nodes.get(nodeId);

    if (!runtime) {
        return '';
    }

    const parent = runtime.parentId ? snapshot.tree.nodes.get(runtime.parentId) : undefined;
    const number = parent?.nhgCode ?? runtime.nhgCode ?? runtime.sectionPath;

    return parent
        ? `${number} ${parent.displayName} · ${runtime.displayName.toLowerCase()}`
        : `${number} ${runtime.displayName}`;
}

export interface CostBlocks {
    direct: number;
    term: number;
    improvement: number;
    total: number;
}

/**
 * De drie kostenblokken in hele centen, zo dat alle bevindingen in het paneel
 * er exact tot optellen.
 *
 * - `total` is het dossiertotaal van de engine, met de eigen bedragen op
 *   antwoorden erin: precies de som van de afgeleide bevindingen;
 * - `manual` zijn de handmatige bevindingen. Die hangen aan geen enkele
 *   antwoordoptie, dus de engine kent ze niet; hun bedragen komen er hier bij,
 *   ook als ze geen onderdeel hebben.
 *
 * Samen is dat precies `inspections.totals` van de API; de gedeelde fixture
 * `__fixtures__/manual-findings-parity.json` bewaakt dat aan beide kanten.
 */
export function costBlocks(
    total: CostAmounts,
    manual: readonly Pick<FindingView, 'costs'>[] = [],
): CostBlocks {
    const costs = manual.reduce<CostCents>((sum, finding) => addCosts(sum, finding.costs), {
        direct: total.direct,
        term: total.term,
        improvement: total.improvement,
    });

    return { ...costs, total: totalCents(costs) };
}

/** `1.250` en `12.345.678`: punten die alleen duizendtallen scheiden. */
const THOUSANDS_ONLY = /^-?[1-9]\d{0,2}(?:\.\d{3})+$/;
const PLAIN_NUMBER = /^-?(?:\d+\.?\d*|\.\d+)$/;

/**
 * Een getal zoals de inspecteur het typt, in Nederlandse notatie: `3,5`,
 * `1.250` en `1.250,5`. Staat er geen komma, dan is een punt alleen een
 * duizendtal-scheiding als het hele getal zo geschreven is (`1.250`); anders is
 * hij het decimaalteken, zodat `2.5` van een Engels toetsenbord 2,5 blijft en
 * geen 25 wordt. De hoeveelheid telt met decimalen mee in de kosten. Onleesbaar
 * levert `null`.
 */
export function parseNumberInput(raw: string): number | null {
    const text = raw.replace(/\s/g, '');
    const normalised = text.includes(',')
        ? text.replace(/\./g, '').replace(',', '.')
        : THOUSANDS_ONLY.test(text)
          ? text.replace(/\./g, '')
          : text;

    if (!PLAIN_NUMBER.test(normalised)) {
        return null;
    }

    const value = Number(normalised);

    return Number.isFinite(value) ? value : null;
}

/**
 * Het volgende antwoord na één tik. Dezelfde optie opnieuw tikken wist hem.
 * Bij een meerkeuzevraag wist een exclusieve optie ("Geen gebreken") de andere
 * selecties, en omgekeerd.
 *
 * `selected` is wat de tik wil: de optie aan (`true`) of uit (`false`), zoals de
 * rij het op het scherm liet zien. Het antwoord zelf kan intussen al verder zijn
 * dan het scherm - een tweede tik voordat de eerste zichtbaar werd - en dan
 * bouwt de tik daarop voort in plaats van het terug te draaien. Zonder
 * `selected` wisselt de tik de optie om.
 */
export function nextAnswer(
    question: PackageQuestion,
    current: Answer | undefined,
    optionId: number,
    selected?: boolean,
): Answer {
    const chosen = current?.option_ids ?? [];
    const select = selected ?? !chosen.includes(optionId);

    if (!select) {
        return { option_ids: chosen.filter((id) => id !== optionId) };
    }

    const multi = question.type === 'options_multi' || question.type === 'options_single_multi';

    if (!multi) {
        return { option_ids: [optionId] };
    }

    if (chosen.includes(optionId)) {
        return { option_ids: [...chosen] };
    }

    const exclusive = new Set(
        question.options.filter((option) => option.is_exclusive).map((option) => option.id),
    );

    return {
        option_ids: exclusive.has(optionId)
            ? [optionId]
            : [...chosen.filter((id) => !exclusive.has(id)), optionId],
    };
}
